import React, { useEffect, useRef, useState } from 'react';
import { uploadFile } from '../../api/portal';
import SelectMenu from '../shared/SelectMenu';
import FileChip from './FileChip';

/**
 * AskSheet — "Ask for something" (Epic 10 · S10.1, spec Part E.9). A form sheet
 * opened from the header "Ask" button (hidden when `!can_write`) and the
 * TrackerPanel empty-state CTA. Fields (Part E.9): Park (all-parks view only,
 * required there), What do you need? (required, one line), Details (optional,
 * multiline), Need it by (optional date, NOT in the past), Files (uploadFile +
 * FileChips, like Composer). Raised messages (Part A.6) surface VERBATIM next to
 * the control: no title → "say what you need"; past need-by → "the date you need
 * it by has already passed"; team member → "the team adds work from ClientHub…"
 * (backend raise, caught + surfaced — defence-in-depth; the button is hidden
 * for !can_write). `onSubmit({ parkId, title, detail, needBy, attachmentPaths })`
 * is a prop so PortalPage owns the `askForSomething` call + close + refetch (the
 * single data-access surface, Epic 1); it throws verbatim on a backend raise.
 * `onUpload` is injectable for tests. Controlled: `open` + `onClose`.
 */
const todayStr = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const AskSheet = ({ open, onClose, parks = [], parkId, onSubmit, onUpload }) => {
  const titleRef = useRef(null);
  const fileInput = useRef(null);
  const [selectedPark, setSelectedPark] = useState('');
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [needBy, setNeedBy] = useState('');
  const [files, setFiles] = useState([]);
  const [titleErr, setTitleErr] = useState(null);
  const [needByErr, setNeedByErr] = useState(null);
  const [parkErr, setParkErr] = useState(null);
  const [submitErr, setSubmitErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const allParks = Array.isArray(parks) && parks.length > 1 && parkId === null;

  // Reset the form each time the sheet opens; focus the title.
  useEffect(() => {
    if (!open) return;
    setTitle(''); setDetail(''); setNeedBy(''); setFiles([]);
    setTitleErr(null); setNeedByErr(null); setParkErr(null); setSubmitErr(null);
    setSelectedPark(allParks ? (parks[0]?.client_id ?? parks[0]?.id ?? '') : (parkId ?? ''));
    const t = setTimeout(() => titleRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape closes (Part E.9 / house pattern).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const attach = async (fileList) => {
    const arr = Array.from(fileList || []);
    for (const file of arr) {
      const id = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setFiles((s) => [...s, { id, name: file.name, size: file.size, type: file.type, progress: 0, uploading: true }]);
      try {
        const path = await (onUpload || uploadFile)(selectedPark || parkId, file);
        setFiles((s) => s.map((f) => (f.id === id ? { ...f, path, progress: 100, uploading: false } : f)));
      } catch (e) {
        setFiles((s) => s.map((f) => (f.id === id ? { ...f, uploading: false, progress: 100, error: e?.message || 'Upload failed' } : f)));
        setSubmitErr(e?.message || 'Upload failed.');
      }
    }
  };

  const submit = async () => {
    setSubmitErr(null); setTitleErr(null); setNeedByErr(null); setParkErr(null);
    // Client-side Part A.6 validations — VERBATIM next to the control.
    if (!title.trim()) { setTitleErr('say what you need'); return; }
    if (needBy && needBy < todayStr()) { setNeedByErr('the date you need it by has already passed'); return; }
    if (allParks && !selectedPark) { setParkErr('Pick a park'); return; }
    if (files.some((f) => f.uploading)) return; // wait for uploads
    setSubmitting(true);
    try {
      const paths = files.filter((f) => f.path).map((f) => f.path);
      await onSubmit?.({
        parkId: allParks ? selectedPark : parkId,
        title: title.trim(),
        detail: detail.trim() || null,
        needBy: needBy || null,
        attachmentPaths: paths,
      });
      // Parent closes + refetches on success.
    } catch (e) {
      setSubmitErr(e?.message || 'Couldn’t ask.'); // verbatim backend raise
    } finally {
      setSubmitting(false);
    }
  };

  const parkOptions = (Array.isArray(parks) ? parks : []).map((p) => ({
    value: p?.client_id ?? p?.id, label: p?.name || 'Park',
  }));

  return (
    <div data-testid="ask-sheet" role="dialog" aria-modal="true" aria-label="Ask for something"
      className="fixed inset-x-0 bottom-0 top-0 md:inset-y-0 md:right-0 md:left-auto w-full md:w-[480px] md:max-w-[calc(100vw-60px)] z-50 flex flex-col bg-white dark:bg-gray-900 shadow-2xl">
      <div className="absolute inset-0 -z-10 bg-black/40 md:hidden" onClick={onClose} aria-hidden="true" />
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Ask for something</h2>
        <button type="button" onClick={onClose} aria-label="Close"
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-2 -mr-2">✕</button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {allParks && (
          <div>
            <label htmlFor="ask-park" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Park <span className="text-red-500" aria-hidden="true">*</span>
            </label>
            <SelectMenu ariaLabel="Park" value={selectedPark} onChange={setSelectedPark} options={parkOptions} placeholder="Select a park…" />
            {parkErr && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert" data-testid="ask-park-error">{parkErr}</p>
            )}
          </div>
        )}

        <div>
          <label htmlFor="ask-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            What do you need? <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input ref={titleRef} id="ask-title" type="text" value={title} disabled={submitting}
            onChange={(e) => setTitle(e.target.value)} placeholder="One line — what do you need?"
            aria-label="What do you need?"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]" />
          {titleErr && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert" data-testid="ask-title-error">{titleErr}</p>
          )}
        </div>

        <div>
          <label htmlFor="ask-detail" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Details <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
          </label>
          <textarea id="ask-detail" value={detail} disabled={submitting} onChange={(e) => setDetail(e.target.value)}
            rows={3} placeholder="Anything else the team should know?" aria-label="Details"
            className="w-full resize-y rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]" />
        </div>

        <div>
          <label htmlFor="ask-needby" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Need it by <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
          </label>
          <input id="ask-needby" type="date" value={needBy} min={todayStr()} disabled={submitting}
            onChange={(e) => setNeedBy(e.target.value)} aria-label="Need it by"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]" />
          {needByErr && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert" data-testid="ask-needby-error">{needByErr}</p>
          )}
        </div>

        <div>
          <input ref={fileInput} type="file" multiple className="hidden"
            onChange={(e) => { attach(e.target.files); e.target.value = ''; }} data-testid="ask-file-input" />
          <button type="button" onClick={() => fileInput.current?.click()} disabled={submitting}
            className="inline-flex items-center justify-center min-h-[44px] px-3 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            aria-label="Attach files">Attach</button>
          {files.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {files.map((f) => (<FileChip key={f.id} file={f} />))}
            </div>
          )}
        </div>

        {submitErr && (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert" data-testid="ask-submit-error">{submitErr}</p>
        )}
      </div>

      <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={submitting}
          className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50">
          Cancel
        </button>
        <button type="button" onClick={submit} disabled={submitting || files.some((f) => f.uploading)}
          data-testid="ask-submit"
          className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-lg text-sm font-medium text-white bg-[#32B1FF] hover:bg-[#1aa3f5] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]">
          {submitting ? 'Sending…' : 'Ask'}
        </button>
      </div>
    </div>
  );
};

export default AskSheet;

